// AI Workflow Settings Component
// Displays and manages AI workflows, prompts, and agents

import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api/client'
import { useSettingsNavigationStore } from '@/stores/settingsStore'
import { PromptEditor } from './PromptEditor'
import { WorkflowEditor } from './WorkflowEditor'
import { AgentEditor } from './AgentEditor'
import {
  Workflow,
  MessageSquare,
  Bot,
  ChevronRight,
  Play,
  Pause,
  Settings2,
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
  FileText,
  Users,
  AlertTriangle,
  Lightbulb,
  Briefcase,
  Filter,
  X,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface AIWorkflow {
  id: string
  key: string
  name: string
  description: string | null
  icon: string
  color: string
  isActive: boolean
  isSystem: boolean
  sortOrder: number
}

interface AIPrompt {
  id: string
  key: string
  name: string
  description: string | null
  category: string
  icon: string
  color: string | null
  isActive: boolean
}

interface AIAgent {
  id: string
  key: string
  name: string
  description: string | null
  icon: string
  color: string | null
  triggerType: string
  isActive: boolean
}

type SettingsSection = 'workflows' | 'prompts' | 'agents'

// ============================================================================
// ICON HELPER
// ============================================================================

function getIcon(iconName: string, className: string = 'w-4 h-4') {
  const icons: Record<string, React.ReactNode> = {
    compass: <Sparkles className={className} />,
    'graduation-cap': <Sparkles className={className} />,
    'file-text': <FileText className={className} />,
    users: <Users className={className} />,
    'alert-triangle': <AlertTriangle className={className} />,
    sparkles: <Sparkles className={className} />,
    lightbulb: <Lightbulb className={className} />,
    briefcase: <Briefcase className={className} />,
    'message-square': <MessageSquare className={className} />,
    'book-open': <FileText className={className} />,
    'message-circle': <MessageSquare className={className} />,
    'bar-chart-2': <Settings2 className={className} />,
    bot: <Bot className={className} />,
    calendar: <Settings2 className={className} />,
    search: <Settings2 className={className} />,
    workflow: <Workflow className={className} />,
  }
  return icons[iconName] || <Settings2 className={className} />
}

// ============================================================================
// DATA FETCHING
// ============================================================================

async function fetchWorkflows(): Promise<AIWorkflow[]> {
  const response = await apiClient.get<{ workflows: AIWorkflow[] }>('/api/workflows?activeOnly=false')
  return response.data.workflows
}

async function fetchPrompts(filters?: { workflow?: string; category?: string }): Promise<AIPrompt[]> {
  const params = new URLSearchParams()
  if (filters?.workflow) params.set('workflow', filters.workflow)
  if (filters?.category) params.set('category', filters.category)
  const queryString = params.toString()
  const response = await apiClient.get<{ prompts: AIPrompt[] }>(`/api/prompts${queryString ? `?${queryString}` : ''}`)
  return response.data.prompts
}

async function fetchAgents(): Promise<AIAgent[]> {
  const response = await apiClient.get<{ agents: AIAgent[] }>('/api/agents?activeOnly=false')
  return response.data.agents
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AIWorkflowSettings() {
  const [activeSection, setActiveSection] = useState<SettingsSection>('workflows')
  const [selectedPromptKey, setSelectedPromptKey] = useState<string | null>(null)
  const [selectedWorkflowKey, setSelectedWorkflowKey] = useState<string | null>(null)
  const [selectedAgentKey, setSelectedAgentKey] = useState<string | null>(null)

  // Filter state
  const [workflowFilter, setWorkflowFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  const clearFilters = () => {
    setWorkflowFilter('')
    setCategoryFilter('')
  }

  // Listen for navigation from Execution Logs (to edit a specific prompt)
  const targetSection = useSettingsNavigationStore((state) => state.targetSection)
  const targetPromptKey = useSettingsNavigationStore((state) => state.targetPromptKey)
  const clearNavigation = useSettingsNavigationStore((state) => state.clearNavigation)

  useEffect(() => {
    if (targetSection === 'prompts' && targetPromptKey) {
      setActiveSection('prompts')
      setSelectedPromptKey(targetPromptKey)
      clearNavigation()
    } else if (targetSection) {
      setActiveSection(targetSection)
      clearNavigation()
    }
  }, [targetSection, targetPromptKey, clearNavigation])

  const hasFilters = workflowFilter || categoryFilter

  const {
    data: workflows,
    isLoading: workflowsLoading,
    error: workflowsError,
    refetch: refetchWorkflows,
  } = useQuery({
    queryKey: ['ai-workflows'],
    queryFn: fetchWorkflows,
  })

  const {
    data: prompts,
    isLoading: promptsLoading,
    error: promptsError,
    refetch: refetchPrompts,
  } = useQuery({
    queryKey: ['ai-prompts', workflowFilter, categoryFilter],
    queryFn: () => fetchPrompts({
      workflow: workflowFilter || undefined,
      category: categoryFilter || undefined
    }),
  })

  const {
    data: agents,
    isLoading: agentsLoading,
    error: agentsError,
    refetch: refetchAgents,
  } = useQuery({
    queryKey: ['ai-agents'],
    queryFn: fetchAgents,
  })

  // Extract unique categories from prompts for the category filter
  const allCategories = useMemo(() => {
    if (!prompts) return []
    const categories = new Set(prompts.map((p) => p.category))
    return Array.from(categories).sort()
  }, [prompts])

  const handleRefresh = () => {
    refetchWorkflows()
    refetchPrompts()
    refetchAgents()
  }

  const sections = [
    {
      id: 'workflows' as SettingsSection,
      label: 'Workflows',
      icon: <Workflow className="w-4 h-4" />,
      count: workflows?.length ?? 0,
    },
    {
      id: 'prompts' as SettingsSection,
      label: 'Prompts',
      icon: <MessageSquare className="w-4 h-4" />,
      count: prompts?.length ?? 0,
    },
    {
      id: 'agents' as SettingsSection,
      label: 'Agents',
      icon: <Bot className="w-4 h-4" />,
      count: agents?.length ?? 0,
    },
  ]

  return (
    <div className="h-full overflow-y-auto space-y-6 pb-4">
      {/* Section Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-market/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-market" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">AI Workflows & Prompts</CardTitle>
                <CardDescription className="text-text-muted">
                  Manage discovery workflows, prompt templates, and automation agents
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Section Tabs */}
          <div className="flex gap-2 border-b border-border-subtle pb-3">
            {sections.map((section) => {
              const isActive = activeSection === section.id
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-market/10 text-market'
                      : 'text-text-muted hover:text-text hover:bg-warm-100'
                  }`}
                >
                  {section.icon}
                  {section.label}
                  <Badge
                    variant="secondary"
                    className={`ml-1 ${isActive ? 'bg-market/20 text-market' : ''}`}
                  >
                    {section.count}
                  </Badge>
                </button>
              )
            })}
          </div>

          {/* Section Content */}
          <div className="mt-4">
            {activeSection === 'workflows' && (
              selectedWorkflowKey ? (
                <WorkflowEditor
                  workflowKey={selectedWorkflowKey}
                  onClose={() => setSelectedWorkflowKey(null)}
                  onSelectPrompt={(key) => {
                    setSelectedPromptKey(key)
                    setActiveSection('prompts')
                  }}
                />
              ) : (
                <WorkflowsSection
                  workflows={workflows ?? []}
                  isLoading={workflowsLoading}
                  error={workflowsError}
                  onSelectWorkflow={(key) => setSelectedWorkflowKey(key)}
                />
              )
            )}
            {activeSection === 'prompts' && (
              selectedPromptKey ? (
                <PromptEditor
                  promptKey={selectedPromptKey}
                  onClose={() => setSelectedPromptKey(null)}
                />
              ) : (
                <PromptsSection
                  prompts={prompts ?? []}
                  workflows={workflows ?? []}
                  isLoading={promptsLoading}
                  error={promptsError}
                  onSelectPrompt={(key) => setSelectedPromptKey(key)}
                  workflowFilter={workflowFilter}
                  categoryFilter={categoryFilter}
                  onWorkflowFilterChange={setWorkflowFilter}
                  onCategoryFilterChange={setCategoryFilter}
                  onClearFilters={clearFilters}
                  hasFilters={!!hasFilters}
                />
              )
            )}
            {activeSection === 'agents' && (
              selectedAgentKey ? (
                <AgentEditor
                  agentKey={selectedAgentKey}
                  onClose={() => setSelectedAgentKey(null)}
                />
              ) : (
                <AgentsSection
                  agents={agents ?? []}
                  isLoading={agentsLoading}
                  error={agentsError}
                  onSelectAgent={(key) => setSelectedAgentKey(key)}
                />
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

interface WorkflowsSectionProps {
  workflows: AIWorkflow[]
  isLoading: boolean
  error: Error | null
  onSelectWorkflow: (key: string) => void
}

function WorkflowsSection({ workflows, isLoading, error, onSelectWorkflow }: WorkflowsSectionProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-solution py-4">
        <AlertCircle className="w-5 h-5" />
        <span>Failed to load workflows. Make sure the API is running.</span>
      </div>
    )
  }

  if (workflows.length === 0) {
    return (
      <div className="text-center py-8 text-text-muted">
        <Workflow className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No workflows configured</p>
        <p className="text-sm mt-1">Run the seed command to create default workflows.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {workflows.map((workflow) => (
        <div
          key={workflow.id}
          onClick={() => onSelectWorkflow(workflow.key)}
          className="flex items-center justify-between p-4 bg-warm-50 rounded-lg border border-border-subtle hover:border-warm-300 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${workflow.color}20` }}
            >
              <span style={{ color: workflow.color }}>
                {getIcon(workflow.icon, 'w-5 h-5')}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-text">{workflow.name}</span>
                {workflow.isSystem && (
                  <Badge variant="outline" className="text-xs">
                    System
                  </Badge>
                )}
              </div>
              <p className="text-sm text-text-muted">{workflow.description || 'No description'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              className={
                workflow.isActive
                  ? 'bg-market/10 text-market border border-market/20'
                  : 'bg-warm-100 text-text-muted'
              }
            >
              {workflow.isActive ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Active
                </>
              ) : (
                <>
                  <Pause className="w-3 h-3 mr-1" />
                  Inactive
                </>
              )}
            </Badge>
            <ChevronRight className="w-4 h-4 text-text-muted" />
          </div>
        </div>
      ))}
    </div>
  )
}

interface PromptsSectionProps {
  prompts: AIPrompt[]
  workflows: AIWorkflow[]
  isLoading: boolean
  error: Error | null
  onSelectPrompt: (key: string) => void
  workflowFilter: string
  categoryFilter: string
  onWorkflowFilterChange: (value: string) => void
  onCategoryFilterChange: (value: string) => void
  onClearFilters: () => void
  hasFilters: boolean
}

function PromptsSection({
  prompts,
  workflows,
  isLoading,
  error,
  onSelectPrompt,
  workflowFilter,
  categoryFilter,
  onWorkflowFilterChange,
  onCategoryFilterChange,
  onClearFilters,
  hasFilters,
}: PromptsSectionProps) {
  // Get unique categories from current prompts (unfiltered would need separate query)
  const categories = useMemo(() => {
    const cats = new Set<string>()
    // Add common categories
    cats.add('discovery')
    cats.add('discovery-coach')
    cats.add('coach')
    cats.add('analysis')
    cats.add('general')
    prompts.forEach((p) => cats.add(p.category))
    return Array.from(cats).sort()
  }, [prompts])

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 p-3 bg-warm-50 rounded-lg border border-border-subtle">
        <Filter className="w-4 h-4 text-text-muted flex-shrink-0" />
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          {/* Workflow Filter */}
          <Select value={workflowFilter || 'all'} onValueChange={(v) => onWorkflowFilterChange(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px] h-8 text-sm">
              <SelectValue placeholder="All Workflows" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Workflows</SelectItem>
              {workflows.map((w) => (
                <SelectItem key={w.key} value={w.key}>
                  {w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Category Filter */}
          <Select value={categoryFilter || 'all'} onValueChange={(v) => onCategoryFilterChange(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[160px] h-8 text-sm">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  <span className="capitalize">{cat.replace('-', ' ')}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-8 px-2 text-text-muted hover:text-text"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Result count */}
        <span className="text-xs text-text-muted flex-shrink-0">
          {prompts.length} prompt{prompts.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="flex items-center gap-2 text-solution py-4">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load prompts. Make sure the API is running.</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && prompts.length === 0 && (
        <div className="text-center py-8 text-text-muted">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
          {hasFilters ? (
            <>
              <p className="font-medium">No prompts match filters</p>
              <p className="text-sm mt-1">Try adjusting your filter criteria.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                className="mt-3"
              >
                Clear Filters
              </Button>
            </>
          ) : (
            <>
              <p className="font-medium">No prompts configured</p>
              <p className="text-sm mt-1">Run the seed command to create default prompts.</p>
            </>
          )}
        </div>
      )}

      {/* Prompts List */}
      {!isLoading && !error && prompts.length > 0 && (
        <PromptsList prompts={prompts} onSelectPrompt={onSelectPrompt} />
      )}
    </div>
  )
}

// Separate component for the prompts list grouped by category
function PromptsList({
  prompts,
  onSelectPrompt,
}: {
  prompts: AIPrompt[]
  onSelectPrompt: (key: string) => void
}) {
  // Group by category
  const byCategory = prompts.reduce(
    (acc, prompt) => {
      const cat = prompt.category || 'general'
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(prompt)
      return acc
    },
    {} as Record<string, AIPrompt[]>
  )

  return (
    <div className="space-y-6">
      {Object.entries(byCategory).map(([category, categoryPrompts]) => (
        <div key={category}>
          <h4 className="text-sm font-medium text-text-secondary mb-3 capitalize">
            {category.replace('-', ' ')}
            <Badge variant="secondary" className="ml-2 text-xs">
              {categoryPrompts.length}
            </Badge>
          </h4>
          <div className="space-y-2">
            {categoryPrompts.map((prompt) => (
              <div
                key={prompt.id}
                onClick={() => onSelectPrompt(prompt.key)}
                className="flex items-center justify-between p-3 bg-warm-50 rounded-lg border border-border-subtle hover:border-warm-300 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: prompt.color ? `${prompt.color}20` : 'var(--color-warm-100)' }}
                  >
                    <span style={{ color: prompt.color || 'var(--color-text-muted)' }}>
                      {getIcon(prompt.icon, 'w-4 h-4')}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-text text-sm">{prompt.name}</span>
                    <p className="text-xs text-text-muted">{prompt.description || 'No description'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {prompt.key}
                  </Badge>
                  <ChevronRight className="w-4 h-4 text-text-muted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

interface AgentsSectionProps {
  agents: AIAgent[]
  isLoading: boolean
  error: Error | null
  onSelectAgent: (key: string) => void
}

function AgentsSection({ agents, isLoading, error, onSelectAgent }: AgentsSectionProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-solution py-4">
        <AlertCircle className="w-5 h-5" />
        <span>Failed to load agents. Make sure the API is running.</span>
      </div>
    )
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-8 text-text-muted">
        <Bot className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No agents configured</p>
        <p className="text-sm mt-1">Run the seed command to create default agents.</p>
      </div>
    )
  }

  const triggerLabels: Record<string, string> = {
    on_enter: 'On Phase Enter',
    on_action: 'On Action',
    on_complete: 'On Complete',
    on_schedule: 'Scheduled',
    on_event: 'On Event',
    on_request: 'On Request',
  }

  return (
    <div className="space-y-3">
      {agents.map((agent) => (
        <div
          key={agent.id}
          onClick={() => onSelectAgent(agent.key)}
          className="flex items-center justify-between p-4 bg-warm-50 rounded-lg border border-border-subtle hover:border-warm-300 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: agent.color ? `${agent.color}20` : 'var(--color-business)/10' }}
            >
              <span style={{ color: agent.color || 'var(--color-business)' }}>
                {getIcon(agent.icon, 'w-5 h-5')}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-text">{agent.name}</span>
                <Badge variant="outline" className="text-xs">
                  {triggerLabels[agent.triggerType] || agent.triggerType}
                </Badge>
              </div>
              <p className="text-sm text-text-muted">{agent.description || 'No description'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              className={
                agent.isActive
                  ? 'bg-market/10 text-market border border-market/20'
                  : 'bg-warm-100 text-text-muted'
              }
            >
              {agent.isActive ? (
                <>
                  <Play className="w-3 h-3 mr-1" />
                  Running
                </>
              ) : (
                <>
                  <Pause className="w-3 h-3 mr-1" />
                  Paused
                </>
              )}
            </Badge>
            <ChevronRight className="w-4 h-4 text-text-muted" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default AIWorkflowSettings
